import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Label } from '@/components/Label';
import { Typography } from '@/components/Typography';
import { SiteAddressCard } from '@/elements/jobDetails/SiteAddressCard';
import { SiteContactCard } from '@/elements/jobDetails/SiteContactCard';
import { useDefinedParams } from '@/hooks/useDefinedParams';
import { useBookingPayload } from '@/hooks/useBookingPayload';
import { CancelJobButton } from '@/elements/CancelJobButton';

export interface props {
  bookingId: number;
}

export const BookingSuccess = (props: props) => {
  const queryClient = useQueryClient();

  const [mpan, serviceLine] = useDefinedParams(['mpan', 'serviceLine']);
  const nav = useNavigate();
  //To Save us constructing our our own data to use for displaying on the review screen
  //we just make use of the .MakeMessage which gives us access to all the data we need to display
  //apart from the bookingId which will be passed into this component.
  const bookingDataToRender = useBookingPayload();
  if (!bookingDataToRender) return null;

  const standardPadding = 'p-4';
  const bookingId = props.bookingId.toString();

  //needed for type inference
  const windowDateTime: DateTime = DateTime.fromISO(bookingDataToRender.StartTime);

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const newWindowDateTime = windowDateTime.toFormat('dd/MM/yyyy') || '';

  const routeToSiteScreen = () => {
    queryClient.invalidateQueries({ queryKey: [`site-${mpan}-${serviceLine}`] });
    queryClient.invalidateQueries({ queryKey: [`get-booking-list-${mpan}-${serviceLine}`] });
    nav(`/site/${mpan}/${serviceLine}`);
  };

  const routeToSearchScreen = () => {
    nav(`/`);
  };

  return (
    <div id="jbp-job-screen" className="mx-10 mt-6">
      <div className="flex flex-row justify-between mb-4 items-center">
        <Typography content={'Booking Confirmed. Please quote the booking reference below when raising any tickets related to this job.'} className="mt-2"></Typography>
        <div className="flex flex-row gap-4 items-center">
          <CancelJobButton bookingId={bookingId} mpan={mpan} serviceLine={serviceLine} />
          <Button text="Back to Site Screen" primary={true} onClick={routeToSiteScreen} className="mt-2 text-nowrap" disabled={false} />
          <Button text="Back to Search Screen" primary={true} onClick={routeToSearchScreen} className="mt-2 text-nowrap" disabled={false} />
        </div>
      </div>
      <Card render={true} identifier="jobBooked" className={`${standardPadding} mb-6`}>
        <Label title="Booking Reference" text={bookingId}></Label>
      </Card>
      <div className="grid grid-cols-3 gap-6">
        <SiteAddressCard siteAddress={bookingDataToRender.SiteAddress} disableCollapse />
        {bookingDataToRender.SiteContactDetails == undefined ? <span></span> : <SiteContactCard contactDetails={bookingDataToRender.SiteContactDetails} disableCollapse />}
        <div className="flex flex-col gap-2">
          <Card render={true} identifier="mpan" className={`${standardPadding}`}>
            <Label title="MPXN" text={mpan}></Label>
          </Card>
          <Card render={bookingDataToRender.BookingType === 'windowBooking'} identifier="apptTime" className={`${standardPadding}`}>
            <Label title="Booking Type" text={'Window Booking'}></Label>
          </Card>
          <Card render={bookingDataToRender.ScheduledDate != null} identifier="apptTime" className={`${standardPadding}`}>
            <Label title="Appointment Time" text={bookingDataToRender.ScheduledDate}></Label>
          </Card>
          <Card render={bookingDataToRender.BookingType === 'windowBooking'} identifier="apptTime" className={`${standardPadding}`}>
            <Label title="Appointment Time" text={newWindowDateTime}></Label>
          </Card>
          <Card render={true} identifier="jobBooked" className={`${standardPadding}`}>
            <Label title="Job Type" text={bookingDataToRender.JobType}></Label>
          </Card>
          <Card render={bookingDataToRender.ExtraSkills !== undefined && bookingDataToRender.ExtraSkills.length > 0} identifier="extraSkills" className={`${standardPadding}`}>
            <Label title="Extra Skills" text={bookingDataToRender.ExtraSkills?.join(', ')}></Label>
          </Card>
        </div>
      </div>
    </div>
  );
};
